import { Inbox } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

function InterestBadge({ value }) {
  if (!value) return <span className="text-muted-foreground">—</span>;
  const map = {
    "Licara": "border-[rgba(20,184,166,0.4)] bg-[rgba(20,184,166,0.12)] text-[hsl(173_70%_60%)]",
    "Website Design": "border-[rgba(129,140,248,0.4)] bg-[rgba(129,140,248,0.12)] text-[hsl(239_84%_78%)]",
    "General inquiry": "border-border bg-secondary text-muted-foreground",
  };
  const cls = map[value] || "border-border bg-secondary text-muted-foreground";
  return (
    <span className={`inline-flex whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium ${cls}`}>
      {value}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

const Empty = ({ span }) => (
  <TableRow>
    <TableCell colSpan={span} className="py-14 text-center text-muted-foreground">
      <Inbox className="mx-auto mb-3 h-8 w-8 text-muted-foreground/60" />
      No submissions yet.
    </TableCell>
  </TableRow>
);

const Shell = ({ children }) => (
  <div className="overflow-x-auto rounded-[var(--radius-card)] border border-border bg-card">{children}</div>
);

export default function SubmissionsPanel({ data }) {
  const counts = data?.counts || { contacts: 0, demos: 0, applications: 0 };
  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-semibold">Submissions</h2>
        <p className="mt-1 text-sm text-muted-foreground">Demo requests, contact messages, and job applications.</p>
      </div>
      <Tabs defaultValue="demos" className="w-full" data-testid="admin-filter-tabs">
        <TabsList className="bg-secondary">
          <TabsTrigger value="demos" data-testid="admin-tab-demos">Demo requests ({counts.demos})</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="admin-tab-contacts">Contact ({counts.contacts})</TabsTrigger>
          <TabsTrigger value="applications" data-testid="admin-tab-applications">Applications ({counts.applications})</TabsTrigger>
        </TabsList>

        <TabsContent value="demos" className="mt-6">
          <Shell>
            <Table data-testid="admin-submissions-table">
              <TableHeader><TableRow className="hover:bg-transparent">
                <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Organization</TableHead><TableHead>Interest</TableHead><TableHead>Type</TableHead><TableHead>Team</TableHead><TableHead>Date</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(data?.demos || []).length === 0 ? <Empty span={7} /> : data.demos.map((d) => (
                  <TableRow key={d.id} className="hover:bg-secondary/40">
                    <TableCell className="font-medium">{d.name}</TableCell>
                    <TableCell className="text-muted-foreground">{d.email}</TableCell>
                    <TableCell className="text-muted-foreground">{d.organization}</TableCell>
                    <TableCell><InterestBadge value={d.interest} /></TableCell>
                    <TableCell className="text-muted-foreground">{d.org_type || "—"}</TableCell>
                    <TableCell className="text-muted-foreground">{d.team_size || "—"}</TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground">{fmtDate(d.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Shell>
        </TabsContent>

        <TabsContent value="contacts" className="mt-6">
          <Shell>
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent">
                <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Organization</TableHead><TableHead>Interest</TableHead><TableHead>Subject</TableHead><TableHead>Message</TableHead><TableHead>Date</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(data?.contacts || []).length === 0 ? <Empty span={7} /> : data.contacts.map((c) => (
                  <TableRow key={c.id} className="hover:bg-secondary/40">
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell className="text-muted-foreground">{c.email}</TableCell>
                    <TableCell className="text-muted-foreground">{c.organization || "—"}</TableCell>
                    <TableCell><InterestBadge value={c.interest} /></TableCell>
                    <TableCell className="text-muted-foreground">{c.subject || "—"}</TableCell>
                    <TableCell className="max-w-xs truncate text-muted-foreground" title={c.message}>{c.message}</TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground">{fmtDate(c.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Shell>
        </TabsContent>

        <TabsContent value="applications" className="mt-6">
          <Shell>
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent">
                <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Location</TableHead><TableHead>Links</TableHead><TableHead>Date</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(data?.applications || []).length === 0 ? <Empty span={6} /> : data.applications.map((a) => (
                  <TableRow key={a.id} className="hover:bg-secondary/40">
                    <TableCell className="font-medium">{a.name}</TableCell>
                    <TableCell className="text-muted-foreground">{a.email}</TableCell>
                    <TableCell className="text-muted-foreground">{a.role}</TableCell>
                    <TableCell className="text-muted-foreground">{a.location || "—"}</TableCell>
                    <TableCell className="text-muted-foreground">
                      <div className="flex gap-3">
                        {a.linkedin && <a href={a.linkedin} target="_blank" rel="noreferrer" className="text-primary hover:underline">LinkedIn</a>}
                        {a.portfolio && <a href={a.portfolio} target="_blank" rel="noreferrer" className="text-primary hover:underline">Portfolio</a>}
                        {!a.linkedin && !a.portfolio && "—"}
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground">{fmtDate(a.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Shell>
        </TabsContent>
      </Tabs>
    </div>
  );
}
